// src/components/profile/ProfileSectionCard.jsx - Enhanced version
import React from 'react';
import { 
  User, Building, Briefcase, Package, Mail, Phone, Globe,
  Award, Calendar, Edit, Trash2, Copy, MoreHorizontal, Users,
  Eye, EyeOff, ArrowUp, ArrowDown, BarChart3, CheckCircle,
  Star, MessageCircle, Image as ImageIcon, Trophy, File
} from 'lucide-react';

export const ProfileSectionCard = ({ 
  section, 
  profileType, 
  viewMode, 
  showEditControls = false,
  isVisible = true,
  sectionStats = {},
  isSelected = false,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  onPreview,
  onMoveUp,
  onMoveDown
}) => {
  const getSectionIcon = (type) => {
    const icons = {
      hero: profileType === 'organization' ? Building : 
            profileType === 'project' ? Briefcase :
            profileType === 'asset' ? Package : User,
      about: User,
      contact: Mail,
      experience: Briefcase,
      education: Calendar,
      skills: Star,
      projects: Briefcase,
      services: Package,
      achievements: Trophy,
      features: CheckCircle,
      gallery: ImageIcon,
      testimonials: MessageCircle,
      references: Users,
      stats: BarChart3
    };
    
    const IconComponent = icons[type];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <span>📄</span>;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800'; 
      case 'hidden': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSectionPreview = () => {
    switch (section.type) {
      case 'hero':
        return (
          <div className="space-y-2">
            {section.name && <p className="font-medium">{section.name}</p>}
            {section.title && <p className="text-sm text-gray-600">{section.title}</p>}
            {section.tagline && <p className="text-sm text-gray-500">{section.tagline}</p>}
          </div>
        );
      case 'about':
        return (
          <p className="text-sm text-gray-600 line-clamp-3">
            {section.description || 'No description available'}
          </p>
        );
      case 'contact':
        return (
          <div className="space-y-1">
            {section.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="h-3 w-3" />
                <span>{section.email}</span>
              </div>
            )}
            {section.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="h-3 w-3" />
                <span>{section.phone}</span>
              </div>
            )}
            {section.website && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Globe className="h-3 w-3" />
                <span>Website</span>
              </div>
            )}
          </div>
        );
      case 'projects':
      case 'services':
      case 'achievements':
      case 'experience':
      case 'education':
      case 'skills':
        const itemCount = section.items?.length || 0;
        return (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              {itemCount} {section.type.slice(0, -1)}{itemCount !== 1 ? 's' : ''}
            </p>
            {section.items?.slice(0, 3).map((item, index) => (
              <div key={index} className="text-xs text-gray-500 truncate">
                {item.title || item.name || item.company || item.institution || 'Item'}
              </div>
            ))}
          </div>
        );
      case 'gallery':
        const mediaCount = section.items?.length || 0;
        return (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{mediaCount} media items</p>
            {section.items?.slice(0, 2).map((item, index) => (
              <div key={index} className="text-xs text-gray-500 truncate">
                {item.title || `Media ${index + 1}`}
              </div>
            ))}
          </div>
        );
      
      case 'references': // NEW
      const referenceCount = section.items?.length || 0;
      const canContactCount = section.items?.filter(ref => ref.canContact !== false).length || 0;
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            {referenceCount} reference{referenceCount !== 1 ? 's' : ''}
          </p>
          {referenceCount > 0 && (
            <div className="space-y-1">
              {canContactCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Users className="h-3 w-3" />
                  <span>{canContactCount} contactable</span>
                </div>
              )}
              {section.items?.some(ref => ref.documentUrl) && (
                <div className="flex items-center gap-1 text-xs text-blue-600">
                  <File className="h-3 w-3" />
                  <span>Documents attached</span>
                </div>
              )}
            </div>
          )}
        </div>
      );

      case 'stats':
        const statsCount = (section.items?.length || 0) + 
                           (section.businessStats ? Object.keys(section.businessStats).length : 0) +
                           (section.personalStats ? Object.keys(section.personalStats).length : 0);
        return (
          <p className="text-sm text-gray-600">{statsCount} statistics</p>
        );
      default:
        return (
          <p className="text-sm text-gray-600 line-clamp-2">
            {section.content || section.description || 'Section content'}
          </p>
        );
    }
  };

  if (viewMode === 'list') {
    return (
      <div className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
        isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:shadow-md'
      }`}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              {showEditControls && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onSelect?.(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
              )}
              
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                  {getSectionIcon(section.type)}
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900 truncate">{section.title}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(section.status)}`}>
                    {section.status}
                  </span>
                  {!isVisible && <EyeOff className="h-4 w-4 text-gray-400" />}
                </div>
                <p className="text-sm text-gray-500 capitalize mb-2">{section.type.replace('-', ' ')}</p>
                <div className="text-sm">
                  {getSectionPreview()}
                </div>
              </div>

              {/* Section Stats */}
              {sectionStats && (
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {sectionStats.views > 0 && (
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>{sectionStats.views}</span>
                    </div>
                  )}
                  {sectionStats.interactions > 0 && (
                    <div className="flex items-center gap-1">
                      <BarChart3 className="h-3 w-3" />
                      <span>{sectionStats.interactions}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {showEditControls && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onMoveUp?.()}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Move up"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onMoveDown?.()}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Move down"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onToggleVisibility?.()}
                  className={`p-1 rounded transition-colors ${
                    isVisible 
                      ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      : 'text-orange-400 hover:text-orange-600 hover:bg-orange-100'
                  }`}
                  title={isVisible ? 'Hide section' : 'Show section'}
                >
                  {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => onPreview?.()}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
                  title="Preview section"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEdit?.()}
                  className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-100 rounded"
                  title="Edit section"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDuplicate?.()}
                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-100 rounded"
                  title="Duplicate section"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete?.()}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded"
                  title="Delete section"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden transition-all group ${
      isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:shadow-md hover:border-gray-300'
    }`}>
      {/* Section Preview/Image */}
      <div className="h-32 bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center relative">
        <div className="text-4xl text-purple-600">
          {getSectionIcon(section.type)}
        </div>
        
        {!isVisible && (
          <div className="absolute top-2 right-2 bg-orange-100 rounded-full p-1">
            <EyeOff className="h-4 w-4 text-orange-600" />
          </div>
        )}

        {showEditControls && (
          <div className="absolute top-2 left-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect?.(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
          </div>
        )}
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate mb-1">{section.title}</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 capitalize">{section.type.replace('-', ' ')}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(section.status)}`}>
                {section.status}
              </span>
            </div>
          </div>

          {showEditControls && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onPreview?.()}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="Preview"
                >
                  <Eye className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onEdit?.()}
                  className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                  title="Edit"
                >
                  <Edit className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onDuplicate?.()}
                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                  title="Duplicate"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onDelete?.()}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content Preview */}
        <div className="text-sm mb-4">
          {getSectionPreview()}
        </div>

        {/* Footer with Stats and Controls */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>Order: {section.order || 0}</span>
            {sectionStats?.views > 0 && (
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                <span>{sectionStats.views}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => onToggleVisibility?.()}
              className={`p-1 rounded transition-colors ${
                isVisible 
                  ? 'text-gray-400 hover:text-gray-600'
                  : 'text-orange-400 hover:text-orange-600'
              }`}
              title={isVisible ? 'Hide' : 'Show'}
            >
              {isVisible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            </button>
            <button
              onClick={() => onMoveUp?.()}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Move up"
            >
              <ArrowUp className="h-3 w-3" />
            </button>
            <button
              onClick={() => onMoveDown?.()}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Move down"
            >
              <ArrowDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};